import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BrandHeroCircle, type BrandHeroMotion, type BrandHeroTone } from './BrandHeroCircle';
import { BrandStatusShell, type BrandStatusTone } from './BrandStatusShell';

export interface BrandStatusAction {
  label: string;
  icon?: LucideIcon;
  /** Router link target. Mutually exclusive with `onClick`. */
  to?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'coral';
}

export interface BrandStatusNavChip {
  label: string;
  to: string;
  icon: LucideIcon;
}

export interface BrandStatusPageProps {
  layout?: 'viewport' | 'embedded';
  tone?: BrandStatusTone;
  heroTone?: BrandHeroTone;
  heroMotion?: BrandHeroMotion;
  heroSize?: 'md' | 'lg';
  showSparkles?: boolean;
  /** When true, `hero` is rendered as-is (no {@link BrandHeroCircle} wrapper). */
  bareHero?: boolean;
  /** Content inside the hero circle (numeral, icon, tooth). */
  hero: ReactNode;
  badge?: ReactNode;
  statusPill?: ReactNode;
  title: string;
  description: string;
  /** Optional block below description (progress bar, error detail card). */
  detail?: ReactNode;
  primaryAction?: BrandStatusAction;
  secondaryAction?: BrandStatusAction;
  navChips?: BrandStatusNavChip[];
  footer?: string;
  showLogo?: boolean;
  className?: string;
}

function ActionButton({ action }: { action: BrandStatusAction }): JSX.Element {
  const Icon = action.icon;
  const variant = action.variant ?? 'primary';
  const classes = cn(
    'inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold shadow-md transition',
    variant === 'primary' && 'bg-brand-500 text-white hover:bg-brand-600',
    variant === 'coral' && 'bg-coral-500 text-white hover:bg-coral-600',
    variant === 'secondary' &&
      'border border-surface-200 bg-white text-ink-700 shadow-soft hover:border-brand-200',
  );

  const content = (
    <>
      {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
      {action.label}
    </>
  );

  if (action.to) {
    return (
      <Link to={action.to} className={cn(classes, 'no-underline')}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={classes}>
      {content}
    </button>
  );
}

/**
 * Config-driven status page composing shell + hero + copy + CTAs.
 */
export function BrandStatusPage({
  layout = 'viewport',
  tone = 'brand',
  heroTone,
  heroMotion = 'float',
  heroSize = 'lg',
  showSparkles = false,
  bareHero = false,
  hero,
  badge,
  statusPill,
  title,
  description,
  detail,
  primaryAction,
  secondaryAction,
  navChips = [],
  footer,
  showLogo = true,
  className,
}: BrandStatusPageProps): JSX.Element {
  return (
    <BrandStatusShell
      layout={layout}
      tone={tone}
      badge={badge}
      showLogo={showLogo}
      className={className}
    >
      {bareHero ? (
        hero
      ) : (
        <BrandHeroCircle
          size={heroSize}
          tone={heroTone ?? tone}
          motion={heroMotion}
          showSparkles={showSparkles}
        >
          {hero}
        </BrandHeroCircle>
      )}

      {statusPill}

      <h1 className="mb-3 font-display text-[28px] font-semibold leading-tight tracking-tight text-ink-900 sm:text-[32px]">
        {title}
      </h1>
      <p className="mx-auto mb-6 max-w-[390px] text-[15px] leading-relaxed text-ink-500">
        {description}
      </p>

      {detail}

      {primaryAction || secondaryAction ? (
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          {primaryAction ? <ActionButton action={primaryAction} /> : null}
          {secondaryAction ? <ActionButton action={secondaryAction} /> : null}
        </div>
      ) : null}

      {navChips.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-2">
          {navChips.map((chip) => {
            const Icon = chip.icon;
            return (
              <Link key={chip.to} to={chip.to} className="bs-nav-chip">
                <Icon className="h-4 w-4" aria-hidden />
                {chip.label}
              </Link>
            );
          })}
        </div>
      ) : null}

      {footer ? <p className="mt-8 text-xs text-ink-400">{footer}</p> : null}
    </BrandStatusShell>
  );
}
